<template>
  <button
    v-bind="$attrs"
    :class="[
      'button',
      `button--size-${size}`,
      `button--theme-${theme}`,
      { 'button--rounded': rounded },
      { 'button--outline': outline },
    ]"
    @click="click"
  >
    <div class="button__content">
      <div
        v-if="hasIcon"
        :class="[
          'button__content-icon',
          { 'button__content-icon--hidden': loading },
        ]"
      >
        <slot name="icon" />
      </div>

      <div
        v-if="hasText"
        :class="[
          'button__content-text',
          { 'button__content-text--hidden': loading },
        ]"
      >
        <slot />
      </div>
      <div
        v-if="hasIconRight"
        :class="[
          'button__content-icon',
          { 'button__content-icon--hidden': loading },
        ]"
      >
        <slot name="iconRight" />
      </div>
    </div>
    <BaseLoading
      v-if="loading"
      :delay="0"
      class="button__loading-indicator"
      :icon-size="size"
    />

    <BaseBadge v-if="badgeText" class="button__badge">
      {{ badgeText }}
    </BaseBadge>
  </button>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import BaseBadge from './BaseBadge.vue'
import BaseLoading from './BaseLoading.vue'

interface Props {
  size?: 'tiny' | 'small' | 'medium' | 'large'
  theme?: 'default' | 'red' | 'green' | 'blue' | 'purple'
  rounded?: boolean
  outline?: boolean
  loading?: boolean
  badgeText?: number
}

const emit = defineEmits<{
  click: []
}>()

withDefaults(defineProps<Props>(), {
  size: 'medium',
  theme: 'default',
  rounded: false,
  outline: false,
  loading: false,
  badgeText: undefined,
})

const slots = useSlots()

const hasText = computed(() => Boolean(slots.default))
const hasIcon = computed(() => Boolean(slots.icon))
const hasIconRight = computed(() => Boolean(slots.iconRight))
const click = () => {
  emit('click')
}
</script>

<style lang="scss" scoped>
.button {
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;
  outline-offset: $spacing-5xs;
  transition: background-color 100ms ease-in-out,
    outline-offset 100ms ease-in-out;

  &:active {
    outline-offset: 0;
  }

  &__loading-indicator {
    position: absolute;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    visibility: visible;
    overflow: hidden;
  }

  &__content-text {
    font-weight: 700;
    line-height: 1;
    text-wrap: nowrap;

    &--hidden {
      opacity: 0;
    }
  }

  &__badge {
    position: absolute;
    top: -$spacing-4xs;
    right: -$spacing-4xs;
  }

  &__content-icon {
    object-fit: contain;

    :deep(svg, img) {
      display: flex;
      align-self: center;
      width: inherit;
      height: inherit;
    }

    &--hidden {
      opacity: 0;
    }
  }

  &--size-tiny {
    padding: $spacing-5xs $spacing-3xs;
    border-radius: $border-radius-xs;

    .button__content {
      gap: $spacing-5xs;
    }

    .button__content-icon {
      width: 14px;
      height: 14px;
    }

    .button__content-text {
      font-size: $font-size-xs;
      font-weight: 700;
      line-height: 14px;
    }
  }

  &--size-small {
    padding: $spacing-5xs $spacing-2xs;
    border-radius: $border-radius-s;

    .button__content {
      gap: $spacing-4xs;
    }

    .button__content-icon {
      width: $spacing-2xs;
      height: $spacing-2xs;
    }

    .button__content-text {
      font-size: $font-size-s;
      line-height: 22px;
    }
  }

  &--size-medium {
    padding: $spacing-4xs $spacing-2xs;
    border-radius: $border-radius-m;

    .button__content {
      gap: $spacing-4xs;
    }

    .button__content-icon {
      width: $spacing-2xs;
      height: $spacing-2xs;
    }

    .button__content-text {
      font-size: $font-size-m;
      line-height: $spacing-xs;
    }
  }

  &--size-large {
    padding: $spacing-3xs $spacing-s;
    border-radius: $border-radius-m;

    .button__content {
      gap: $spacing-3xs;
    }

    .button__content-icon {
      width: $spacing-2xs;
      height: $spacing-2xs;
    }

    .button__content-text {
      font-size: $font-size-m;
      line-height: $spacing-xs;
    }
  }

  &--rounded {
    border-radius: 100px;
  }

  &--theme-default {
    background: none;
    color: $color-text;

    &:hover {
      background: $color-pebble;
    }

    &:active {
      background: $color-paper;
    }

    &.button--outline {
      border-color: $color-border;
      background: $color-canvas;

      &:hover {
        background: $color-pebble;
      }

      &:active {
        background: $color-canvas;
      }
    }
  }

  &--theme-red {
    background: $color-red-50;
    color: $color-canvas;

    &:hover {
      background: $color-red-60;
    }

    &:active {
      background: $color-red-50;
    }

    &.button--outline {
      border-color: $color-border;
      background: $color-canvas;
      color: $color-red-50;

      &:hover {
        background: $color-red-05;
        color: $color-red-60;
      }

      &:active {
        background: $color-canvas;
      }
    }
  }

  &--theme-green {
    background: $color-green-50;
    color: $color-canvas;

    &:hover {
      background: $color-green-60;
    }

    &:active {
      background: $color-green-50;
    }
  }

  &--theme-blue {
    background: $color-blue-45;
    color: $color-canvas;

    &:hover {
      background: $color-blue-60;
    }

    &:active {
      background: $color-blue-45;
    }

    &.button--outline {
      border-color: $color-border;
      background: $color-canvas;
      color: $color-blue-45;

      &:hover {
        background: $color-blue-10;
        color: $color-blue-60;
        border-color: $color-blue-10;
      }

      &:active {
        background: $color-canvas;
        border-color: $color-border;
      }
    }
  }

  &--theme-purple {
    background: $color-purple-50;
    color: $color-canvas;

    &:hover {
      background: $color-purple-60;
    }

    &:active {
      background: $color-purple-50;
    }
  }

  &:disabled {
    background: $color-disabled;
    color: $color-text-light;
    border-color: transparent;
    pointer-events: none;
  }
}
</style>
